import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import logo from '../../assets/FourHand-logo-new.png';
import { useInterfaceStore, Language } from '../../stores/useInterfaceStore';
import { useT } from '../../i18n';
import { FlagBR, FlagES } from '../../components/FlagIcons';
import api from '../../services/api';
import styles from './Login.module.css';

const LANGUAGES: { code: Language; Flag: React.FC<{ size?: number }>; name: string }[] = [
  { code: 'pt', Flag: FlagBR, name: 'Português' },
  { code: 'es', Flag: FlagES, name: 'Español' },
];

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, string>) => void;
        };
      };
    };
  }
}

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setUsuarioId, setTipoEmpresa, clearSession, language, setLanguage } = useInterfaceStore();
  const t = useT();
  const googleButtonRef = useRef<HTMLDivElement>(null);

  const validateFields = () => {
    if (!username.trim()) {
      setError(t('login.errorUsernameRequired'));
      return false;
    }
    if (!password) {
      setError(t('login.errorPasswordRequired'));
      return false;
    }
    if (password.length < 3) {
      setError(t('login.errorPasswordMinLength'));
      return false;
    }
    return true;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateFields()) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.login(username, password);

      if (response.success && response.data) {
        clearSession(); // limpa sessão anterior antes de configurar a nova
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('usuarioData', JSON.stringify(response.data.user));
        setUsuarioId(response.data.user.id);
        setTipoEmpresa(response.data.user.tipo_empresa || 'Empresa Fim');
        navigate('/selecionar-empresa');
      } else {
        setError((response.data as any)?.error || response.error || t('login.errorInvalidCredentials'));
      }
    } catch (err) {
      console.error('Erro de conexão:', err);
      setError(t('login.errorConnection'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = async (googleResponse: { credential: string }) => {
    setError('');
    setIsLoading(true);
    try {
      const response = await api.loginGoogle(googleResponse.credential);

      if (response.success && response.data) {
        clearSession(); // limpa sessão anterior antes de configurar a nova
        localStorage.setItem('authToken', response.data.token);
        localStorage.setItem('usuarioData', JSON.stringify(response.data.user));
        setUsuarioId(response.data.user.id);
        setTipoEmpresa(response.data.user.tipo_empresa || 'Empresa Fim');
        navigate('/selecionar-empresa');
      } else {
        setError((response.data as any)?.error || response.error || t('login.errorGoogle'));
      }
    } catch (err) {
      console.error('Erro no login com Google:', err);
      setError(t('login.errorConnection'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) return;

    // O script do GIS carrega com async/defer, então pode ainda não estar
    // pronto quando este componente monta — aguarda até window.google existir.
    const tryRender = () => {
      if (!window.google || !googleButtonRef.current) return false;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredential,
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline',
        size: 'large',
        width: '100%',
        text: 'signin_with',
      });
      return true;
    };

    if (tryRender()) return;
    const intervalId = setInterval(() => {
      if (tryRender()) clearInterval(intervalId);
    }, 200);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.loginContainer}>
      <div className={styles.langSwitcher} role="group" aria-label={t('login.langSwitcher.label')}>
        {LANGUAGES.map(({ code, Flag, name }) => (
          <button
            key={code}
            type="button"
            className={`${styles.langButton} ${language === code ? styles.langButtonActive : ''}`}
            onClick={() => setLanguage(code)}
            aria-pressed={language === code}
            aria-label={name}
            title={name}
          >
            <Flag size={22} />
          </button>
        ))}
      </div>

      {/* Painel esquerdo: narrativa da marca + radar de conformidade (decorativo) */}
      <div className={styles.panelLeft}>
        <div className={styles.dotgrid} aria-hidden="true" />
        <div className={styles.radarWrap} aria-hidden="true">
          <div className={`${styles.ring} ${styles.r1}`} />
          <div className={`${styles.ring} ${styles.r2}`} />
          <div className={`${styles.ring} ${styles.r3}`} />
          <div className={styles.sweep} />
          <div className={styles.blip} style={{ top: 150, left: 120, animationDelay: '0s' }} />
          <div className={styles.blip} style={{ top: 400, left: 70, animationDelay: '1.6s' }} />
          <div className={styles.blip} style={{ top: 260, left: 280, animationDelay: '3.2s' }} />
        </div>

        <div className={styles.brandTop}>
          <img src={logo} alt="" />
          <div className={styles.brandText}>
            <span className={styles.brandName}>{t('login.brand')}</span>
            <span className={styles.brandSub}>{t('login.brandSub')}</span>
          </div>
        </div>

        <div className={styles.narrative}>
          <div className={styles.eyebrow}>
            <span className={styles.eyebrowDot} />{t('login.eyebrow')}
          </div>
          <h2>{t('login.heroTitle')}</h2>
          <p>{t('login.heroText')}</p>
        </div>

        <div className={styles.trustStrip}>
          <div className={styles.trustPill}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2 4 6v6c0 5 3.5 8.5 8 10 4.5-1.5 8-5 8-10V6l-8-4Z" /></svg>
            {t('login.trust.tls')}
          </div>
          <div className={styles.trustPill}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg>
            {t('login.trust.lgpd')}
          </div>
          <div className={styles.trustPill}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h10" /></svg>
            {t('login.trust.audited')}
          </div>
        </div>
      </div>

      {/* Painel direito: formulário */}
      <div className={styles.panelRight}>
        <Card className={styles.loginCard}>
          <Card.Body>
            <div className={styles.title}>{t('login.title')}</div>
            <div className={styles.subtitle}>{t('login.subtitle')}</div>
            {error && <Alert variant="danger">{error}</Alert>}
            <Form onSubmit={handleLogin}>
              <Form.Group className={styles.formGroup} controlId="username">
                <Form.Label>{t('login.usernameLabel')}</Form.Label>
                <Form.Control
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  disabled={isLoading}
                />
              </Form.Group>

              <Form.Group className={styles.formGroup} controlId="password">
                <Form.Label>{t('login.passwordLabel')}</Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={isLoading}
                />
              </Form.Group>

              <div className="d-grid">
                <Button variant="primary" type="submit" disabled={isLoading} className={styles.submitButton}>
                  <span className={styles.scanline} />
                  {isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : t('login.submitButton')}
                </Button>
              </div>
            </Form>
            {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
              <>
                <div className="text-center text-muted my-3">{t('login.or')}</div>
                <div ref={googleButtonRef} className="d-flex justify-content-center" />
              </>
            )}
            <div className={styles.forgotPassword}>
              <a href="/login">{t('login.forgotPassword')}</a>
            </div>
            <div className={styles.footerNote}>
              <span>{t('login.trust.tls')}</span><span>{t('login.trust.lgpd')}</span><span>{t('login.trust.audited')}</span>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default Login;